/* CredentialRequestPage — public, no auth.
 *
 * Candidate lands here from a credential-request email button. Renders a
 * checklist of credentials to upload, each row independently saveable so
 * the candidate can come back later on a different device. When every
 * required item is uploaded, Submit fires the backend write-back which
 * creates one CandidateCertification per item on Bullhorn.
 *
 * Distinct from CredentialRenewalPage (1 cert, expiry-driven) — this is
 * recruiter-initiated initial onboarding capture of N credentials.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  CheckCircle2,
  ShieldCheck,
  Upload,
  AlertCircle,
  RefreshCw,
  FileText,
  Calendar as CalIcon,
  Hash,
  ChevronLeft,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CredentialRequestPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // Per-item in-progress state — keyed by item.id so the candidate can
  // work on one credential at a time without losing inputs on others.
  // Shape: { [itemId]: { file, issue_date, expiry_date, reference_number, uploading } }
  const [drafts, setDrafts] = useState({});

  const refreshCtx = useCallback(async () => {
    const res = await axios.get(`${API}/api/public/credential-requests/${token}`);
    setCtx(res.data);
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/credential-requests/${token}`);
        if (!alive) return;
        setCtx(res.data);
      } catch (err) {
        if (!alive) return;
        const status = err.response?.status;
        if (status === 410) setError(err.response?.data?.detail || 'This link has expired.');
        else if (status === 404) setError('This request could not be found.');
        else setError('Could not load this credential request. Please try again later.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const updateDraft = (itemId, patch) => {
    setDrafts(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), ...patch } }));
  };

  const handleItemUpload = async (item) => {
    const draft = drafts[item.id] || {};
    if (!draft.file) {
      toast.error('Choose a file first');
      return;
    }
    // Validate expiry date if the credential requires it
    const expiryValue = draft.expiry_date || item.expiry_date || '';
    if (!item.expiry_optional && !expiryValue) {
      toast.error(`Expiry date is required for ${item.bullhorn_category_name}`);
      return;
    }
    updateDraft(item.id, { uploading: true });
    try {
      const fd = new FormData();
      fd.append('file', draft.file);
      if (draft.issue_date) fd.append('issue_date', draft.issue_date);
      if (expiryValue) fd.append('expiry_date', expiryValue);
      if (draft.reference_number) fd.append('reference_number', draft.reference_number);
      await axios.post(
        `${API}/api/public/credential-requests/${token}/items/${item.id}/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
      );
      toast.success(`${item.bullhorn_category_name} uploaded`);
      await refreshCtx();
      // Clear the file from the draft so the file input doesn't stick;
      // metadata fields are preserved server-side and re-rendered.
      updateDraft(item.id, { file: null, uploading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      updateDraft(item.id, { uploading: false });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/public/credential-requests/${token}/submit`);
      setSuccess(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please check the required items.');
    } finally {
      setSubmitting(false);
    }
  };

  // Per-item submit — lets the candidate complete each credential as
  // they go instead of being blocked until they've gathered every
  // single document. The request stays `in_progress` (so they can
  // return later) until the LAST required item is verified, at which
  // point the backend flips status to `completed` automatically.
  const handleSubmitOne = async (item) => {
    // Validate expiry date if required before submitting to Bullhorn
    if (!item.expiry_optional && !item.expiry_date) {
      toast.error(`Please enter an expiry date for ${item.bullhorn_category_name} before submitting`);
      return;
    }
    updateDraft(item.id, { submitting: true });
    try {
      const r = await axios.post(
        `${API}/api/public/credential-requests/${token}/items/${item.id}/submit`,
      );
      toast.success(`${item.bullhorn_category_name} sent to your recruiter`);
      if (r.data?.request_status === 'completed') {
        setSuccess({ status: 'completed', items_succeeded: items.length });
        return;
      }
      await refreshCtx();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit this credential.');
    } finally {
      updateDraft(item.id, { submitting: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold mb-1">Link unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const primary = ctx?.primary_color || '#2D5A27';
  const items = ctx?.items || [];
  const requiredItems = items.filter(i => i.required);
  const requiredDone = requiredItems.filter(i => i.status === 'uploaded' || i.status === 'verified').length;
  const allReadyToSubmit = requiredDone === requiredItems.length;

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: primary }} />
          <h1 className="text-xl font-semibold mb-1">You&apos;re all set</h1>
          <p className="text-sm text-slate-600 mb-4">
            {success.items_succeeded} of {success.items_total} credentials have been sent to {ctx?.company_name || 'your recruiter'}.
          </p>
          <p className="text-xs text-slate-500">
            You can close this page now. We&apos;ll be in touch if anything else is needed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="credential-request-page">
      <Toaster position="top-center" />
      {/* Top banner */}
      <header className="text-white" style={{ background: primary }}>
        <div className="max-w-2xl mx-auto px-5 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider opacity-80">{ctx?.company_name || 'Credentials'}</p>
              <h1 className="text-lg font-semibold">Upload your credentials</h1>
            </div>
          </div>
          {ctx?.candidate_name && (
            <p className="mt-3 text-sm opacity-90">Hi {ctx.candidate_name} — please upload the items below.</p>
          )}
        </div>
      </header>

      {/* Back to portal sub-banner */}
      {(ctx?.portal_token || ctx?.candidate_email) && (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
          <div className="max-w-2xl mx-auto flex items-center justify-end">
            <a
              href={ctx.portal_token
                ? `/portal/access/${ctx.portal_token}`
                : `/portal?email=${encodeURIComponent(ctx.candidate_email)}`}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 hover:underline"
              data-testid="back-to-portal-link"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to All Documents
            </a>
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-5 mt-4">
        {/* Progress bar */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-medium text-slate-700">
              {requiredDone} of {requiredItems.length} required uploaded
            </span>
            <span className="text-slate-500">{items.length} total</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: requiredItems.length === 0 ? '100%' : `${(requiredDone / requiredItems.length) * 100}%`,
                background: primary,
              }}
            />
          </div>
          {ctx?.custom_message && (
            <p className="mt-3 text-xs text-slate-600 bg-slate-50 rounded-lg p-3" data-testid="credreq-custom-message-display">
              {ctx.custom_message}
            </p>
          )}
        </div>

        {/* Items */}
        <div className="space-y-3">
          {items.map((it, i) => {
            const uploaded = it.status === 'uploaded' || it.status === 'verified';
            const draft = drafts[it.id] || {};
            return (
              <div
                key={it.id}
                className={`bg-white rounded-2xl shadow-sm overflow-hidden border ${uploaded ? 'border-emerald-200' : 'border-slate-200'}`}
                data-testid={`credreq-item-${i}`}
              >
                <div className="p-4 flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${uploaded ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                    {uploaded ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Upload className="w-4 h-4 text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-semibold text-sm">{it.bullhorn_category_name.replace(/^\s+/, '')}</h2>
                      {!it.required && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Optional</span>
                      )}
                      {uploaded && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Uploaded</span>
                      )}
                    </div>
                    {it.file_name && (
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> {it.file_name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="px-4 pb-4 space-y-3">
                  {/* File input */}
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500">
                      {uploaded ? 'Replace file (optional)' : 'File (PDF or photo)'}
                    </label>
                    <input
                      type="file"
                      accept={(ctx?.allowed_file_types || ['pdf','jpg','jpeg','png']).map(t => `.${t}`).join(',')}
                      onChange={(e) => updateDraft(it.id, { file: e.target.files?.[0] || null })}
                      className="block w-full mt-1 text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                      data-testid={`credreq-file-${i}`}
                    />
                  </div>

                  {/* Metadata grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                        <CalIcon className="w-3 h-3" /> Issue date
                      </label>
                      <input
                        type="date"
                        value={draft.issue_date ?? it.issue_date ?? ''}
                        onChange={(e) => updateDraft(it.id, { issue_date: e.target.value })}
                        className="block w-full mt-1 text-sm rounded-md border border-slate-300 px-2 py-1.5"
                        data-testid={`credreq-issue-${i}`}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                        <CalIcon className="w-3 h-3" /> Expiry date
                        {it.expiry_optional ? (
                          <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
                        ) : (
                          <span className="text-red-500 normal-case tracking-normal">*</span>
                        )}
                      </label>
                      <input
                        type="date"
                        value={draft.expiry_date ?? it.expiry_date ?? ''}
                        onChange={(e) => updateDraft(it.id, { expiry_date: e.target.value })}
                        className={`block w-full mt-1 text-sm rounded-md border px-2 py-1.5 ${!it.expiry_optional && !(draft.expiry_date ?? it.expiry_date) ? 'border-red-300' : 'border-slate-300'}`}
                        data-testid={`credreq-expiry-${i}`}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                        <Hash className="w-3 h-3" /> Reference
                      </label>
                      <input
                        type="text"
                        value={draft.reference_number ?? it.reference_number ?? ''}
                        onChange={(e) => updateDraft(it.id, { reference_number: e.target.value })}
                        placeholder="Optional"
                        className="block w-full mt-1 text-sm rounded-md border border-slate-300 px-2 py-1.5"
                        data-testid={`credreq-ref-${i}`}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => handleItemUpload(it)}
                      disabled={!draft.file || draft.uploading || (!it.expiry_optional && !(draft.expiry_date ?? it.expiry_date))}
                      className="w-full sm:w-auto rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      style={{ background: primary }}
                      data-testid={`credreq-upload-btn-${i}`}
                    >
                      {draft.uploading ? (
                        <><RefreshCw className="w-4 h-4 animate-spin" /> Uploading…</>
                      ) : (
                        <><Upload className="w-4 h-4" /> {uploaded ? 'Replace' : 'Upload'}</>
                      )}
                    </button>
                    {/* Per-item submit — only relevant once the item is
                        uploaded and hasn't been finalised yet. Lets the
                        candidate send each credential as soon as they
                        have it (e.g. they bring 1 of 5 to a renewal
                        appointment and want to register it now). */}
                    {uploaded && it.status !== 'verified' && (
                      <button
                        type="button"
                        onClick={() => handleSubmitOne(it)}
                        disabled={draft.submitting || (!it.expiry_optional && !it.expiry_date)}
                        className="w-full sm:w-auto rounded-lg px-4 py-2 text-sm font-medium border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        data-testid={`credreq-submit-one-${i}`}
                      >
                        {draft.submitting ? (
                          <><RefreshCw className="w-4 h-4 animate-spin" /> Sending…</>
                        ) : (
                          <><CheckCircle2 className="w-4 h-4" /> Send this one now</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit */}
        <div className="mt-5 bg-white rounded-2xl shadow-sm p-4">
          <p className="text-xs text-slate-500 mb-3">
            When every required item is uploaded, hit Submit and we&apos;ll forward everything to {ctx?.company_name || 'your recruiter'}.
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allReadyToSubmit || submitting}
            className="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ background: primary }}
            data-testid="credreq-submit-btn"
          >
            {submitting ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Submitting…</>
            ) : allReadyToSubmit ? (
              <><CheckCircle2 className="w-4 h-4" /> Submit all</>
            ) : (
              `Upload ${requiredItems.length - requiredDone} more required item${requiredItems.length - requiredDone === 1 ? '' : 's'}`
            )}
          </button>
        </div>
      </main>
    </div>
  );
}
